class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector("form");
    form.addEventListener("input", this.debouncedOnSubmit.bind(this));

    // Add change event listener for checkboxes to ensure they trigger the form submission
    form.addEventListener("change", this.debouncedOnSubmit.bind(this));

    // Add single-select behavior for filter groups
    this.addSingleSelectBehavior();

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  addSingleSelectBehavior() {
    // Add event listeners to make filters behave like radio buttons (single select)
    this.addEventListener("change", (event) => {
      // Handle desktop facet checkboxes only (no mobile filters anymore)
      if (event.target.classList.contains("facet-checkbox-modern")) {
        const filterGroup = event.target.closest(".facets__filter-group");

        if (filterGroup && event.target.checked) {
          // Uncheck all other checkboxes in the same filter group
          const otherCheckboxes = filterGroup.querySelectorAll(
            '.facet-checkbox-modern:not([id="' + event.target.id + '"])'
          );
          otherCheckboxes.forEach((checkbox) => {
            checkbox.checked = false;
            // Re-enable the previously checked checkbox
            checkbox.style.pointerEvents = "auto";
            const label = checkbox.nextElementSibling;
            if (label && label.tagName === "LABEL") {
              label.style.pointerEvents = "auto";
              label.style.cursor = "pointer";
            }
          });

          // Disable the newly checked checkbox to prevent unchecking
          event.target.style.pointerEvents = "none";
          const label = event.target.nextElementSibling;
          if (label && label.tagName === "LABEL") {
            label.style.pointerEvents = "none";
            label.style.cursor = "not-allowed";
          }
        }
      }
    });

    // On initial load, disable any already checked checkboxes
    this.querySelectorAll(".facet-checkbox-modern:checked").forEach(
      (checkbox) => {
        checkbox.style.pointerEvents = "none";
        const label = checkbox.nextElementSibling;
        if (label && label.tagName === "LABEL") {
          label.style.pointerEvents = "none";
          label.style.cursor = "not-allowed";
        }
      }
    );

    // Auto-select first filter if no filters are currently active
    this.selectFirstFilterByDefault();
  }

  selectFirstFilterByDefault() {
    // Check if any filters are already active
    const hasActiveFilters =
      this.querySelectorAll(".facet-checkbox-modern:checked").length > 0;

    // Only auto-select if no filters are active
    if (!hasActiveFilters) {
      const firstFilterGroup = this.querySelector(".facets__filter-group");

      if (firstFilterGroup) {
        const firstCheckbox = firstFilterGroup.querySelector(
          ".facet-checkbox-modern:not([disabled])"
        );

        if (firstCheckbox) {
          // Check the first checkbox
          firstCheckbox.checked = true;

          // Disable pointer events to prevent unchecking
          firstCheckbox.style.pointerEvents = "none";
          const label = firstCheckbox.nextElementSibling;
          if (label && label.tagName === "LABEL") {
            label.style.pointerEvents = "none";
            label.style.cursor = "not-allowed";
          }

          // Trigger form submission to apply the filter and show filtered products
          const form = this.querySelector("form");
          if (form) {
            const formData = new FormData(form);
            const searchParams = new URLSearchParams(formData).toString();
            FacetFiltersForm.renderPage(searchParams, null);
          }
        }
      }
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    if (sections.length === 0) {
      return;
    }

    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    const productGridContainer = document.getElementById(
      "ProductGridContainer"
    );
    const collection = productGridContainer
      ? productGridContainer.querySelector(".collection")
      : null;

    if (collection) {
      collection.classList.add("loading");
    }
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch((error) => {
        console.error("Error fetching filtered products:", error);
        // Remove loading state on error
        const productGridContainer = document.getElementById(
          "ProductGridContainer"
        );
        const collection = productGridContainer
          ? productGridContainer.querySelector(".collection")
          : null;
        if (collection) {
          collection.classList.remove("loading");
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(html, "text/html");
    const newProductGrid = parsedHTML.getElementById("ProductGridContainer");
    const currentProductGrid = document.getElementById("ProductGridContainer");

    if (!newProductGrid || !currentProductGrid) {
      return;
    }

    currentProductGrid.innerHTML = newProductGrid.innerHTML;
  }

  static renderProductCount(html) {
    const parser = new DOMParser();
    const parsedHTML = parser.parseFromString(html, "text/html");
    const newProductCount = parsedHTML.getElementById("ProductCount");
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");

    if (!newProductCount) {
      return;
    }

    if (container) {
      container.innerHTML = newProductCount.innerHTML;
      container.classList.remove("loading");
    }

    if (containerDesktop) {
      containerDesktop.innerHTML = newProductCount.innerHTML;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetElement = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      );
      if (targetElement) {
        targetElement.innerHTML = element.innerHTML;
      }
    });

    // Re-apply single-select behavior to newly rendered content
    FacetFiltersForm.applySingleSelectToRenderedContent();

    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest(".js-filter")
      );

    const active = document.querySelector(".price_range_active_facet");
    const priceIcon = document.querySelector(".price_selected-icon");

    if (priceIcon) {
      if (active != null) {
        priceIcon.classList.add("count-selected");
      } else {
        priceIcon.classList.remove("count-selected");
      }
    }
  }

  static applySingleSelectToRenderedContent() {
    // Apply single-select behavior to all facet forms
    document.querySelectorAll("facet-filters-form").forEach((form) => {
      if (!form.singleSelectApplied) {
        form.singleSelectApplied = true;
        form.addEventListener("change", (event) => {
          // Handle desktop facet checkboxes only (no mobile filters anymore)
          if (event.target.classList.contains("facet-checkbox-modern")) {
            const filterGroup = event.target.closest(".facets__filter-group");

            if (filterGroup && event.target.checked) {
              // Uncheck all other checkboxes in the same filter group
              const otherCheckboxes = filterGroup.querySelectorAll(
                '.facet-checkbox-modern:not([id="' + event.target.id + '"])'
              );
              otherCheckboxes.forEach((checkbox) => {
                checkbox.checked = false;
                // Re-enable the previously checked checkbox
                checkbox.style.pointerEvents = "auto";
                const label = checkbox.nextElementSibling;
                if (label && label.tagName === "LABEL") {
                  label.style.pointerEvents = "auto";
                  label.style.cursor = "pointer";
                }
              });

              // Disable the newly checked checkbox to prevent unchecking
              event.target.style.pointerEvents = "none";
              const label = event.target.nextElementSibling;
              if (label && label.tagName === "LABEL") {
                label.style.pointerEvents = "none";
                label.style.cursor = "not-allowed";
              }
            }
          }
        });
      }

      // Disable any already checked checkboxes in rendered content
      form
        .querySelectorAll(".facet-checkbox-modern:checked")
        .forEach((checkbox) => {
          checkbox.style.pointerEvents = "none";
          const label = checkbox.nextElementSibling;
          if (label && label.tagName === "LABEL") {
            label.style.pointerEvents = "none";
            label.style.cursor = "not-allowed";
          }
        });
    });
  }

  static renderActiveFacets(html) {
    // Active facets section has been removed - no longer needed
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const elementSelectors = [".sorting"];

    elementSelectors.forEach((selector) => {
      const newElement = html.querySelector(selector);
      const currentElement = document.querySelector(selector);

      if (!newElement || !currentElement) return;

      currentElement.innerHTML = newElement.innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelectorAll(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    for (let i = 0; i < targetElement.length; i++) {
      if (sourceElement && targetElement) {
        target.querySelectorAll(".facets__selected")[i].outerHTML =
          source.querySelectorAll(".facets__selected")[i].outerHTML;
      }
    }
  }

  static updateURLHash(searchParams) {
    const newUrl = `${window.location.pathname}${
      searchParams && "?".concat(searchParams)
    }`;
    history.pushState({ searchParams }, "", newUrl);
  }

  static getSections() {
    const productGrid = document.querySelector('[id^="product-grid"]');
    if (!productGrid) {
      return [];
    }
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest("form");
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this))
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    document
      .querySelector(".price_selected-icon")
      .classList.add("count-selected");
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector("a").addEventListener("click", (event) => {
      event.preventDefault();
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define("facet-remove", FacetRemove);
